import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { authenticatedFetch, parseJsonResponse, initializeApiClient } from '../api/apiClient';
import { useAtlasStore } from '../store/atlasStore';

interface User {
  id: string;
  username: string;
  name: string;
  tenant: string;
  roles: string[];
}

interface TenantInfo {
  id: string;
  roles: string[];
  auto_select?: boolean;
}

interface TrustedLoginPayload {
  user: User;
  csrfToken: string;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  csrfToken: string | null;
  isAuthenticated: boolean;
  availableTenants: TenantInfo[];
  checkTenants: (username: string, password: string) => Promise<TenantInfo[]>;
  login: (username: string, password: string, tenant: string) => Promise<void>;
  logout: (options?: { skipNavigation?: boolean }) => Promise<void>;
  register: (username: string, password: string, name: string, tenant: string, secret: string) => Promise<void>;
  addTenant: (username: string, password: string, tenant: string, secret: string) => Promise<void>;
  updateProfile: (name: string) => Promise<void>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<void>;
  setCsrfToken: React.Dispatch<React.SetStateAction<string | null>>;
  hydrateFromTrustedLogin: (payload: TrustedLoginPayload) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);
  const [availableTenants, setAvailableTenants] = useState<TenantInfo[]>([]);
  const navigate = useNavigate();

  const logoutStable = useCallback(async (options?: { skipNavigation?: boolean }) => {
    console.log('Logging out...', options);

    let serverLogoutSucceeded = false;

    try {
      const csrf = csrfToken || document.cookie.split('; ').find(r => r.startsWith('csrf_token='))?.split('=')[1] || '';
      const response = await fetch('/api/auth/logout', {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-CSRF-Token': csrf,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        serverLogoutSucceeded = true;
        console.log('Server-side logout successful.');
      } else if (response.status === 401) {
        // 401 means session was already invalid - treat as successful logout
        serverLogoutSucceeded = true;
        console.log('Session already expired on server.');
      } else {
        // Server error (5xx) or other client error - log but continue with client cleanup
        console.warn('Server logout returned unexpected status:', response.status,
          '- proceeding with client-side cleanup');
      }
    } catch (error) {
      // Network error - log but continue with client cleanup
      console.error('Logout API call failed (network error):', error,
        '- proceeding with client-side cleanup');
    }

    // Always clear client-side state, regardless of server response
    // This ensures user is logged out locally even if server is unreachable
    useAtlasStore.getState().resetSparkState();
    setToken(null);
    setUser(null);
    setCsrfToken(null);
    localStorage.removeItem('user');

    if (!serverLogoutSucceeded) {
      console.warn('Note: Server-side session may still be active. ' +
        'Token will expire naturally or be invalidated on next server restart.');
    }

    if (!options?.skipNavigation) {
      navigate('/login');
    }
  }, [navigate, csrfToken]);

  useEffect(() => {
    initializeApiClient({
      logout: logoutStable,
      setCsrfToken: setCsrfToken,
      getCsrfToken: () => csrfToken,
    });
    console.log('AuthProvider: ApiClient initialized/updated.');
  }, [logoutStable, csrfToken]);

  // --- NEW: initialise CSRF token from cookie once on mount ---
  useEffect(() => {
    const cookieToken = document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrf_token='))
      ?.split('=')[1];
    if (cookieToken) {
      setCsrfToken(cookieToken);
    }
  }, []);

  useEffect(() => {
    const verifyAuth = async () => {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        setUser(JSON.parse(storedUser));
        setToken('token-in-cookie');

        try {
          const response = await authenticatedFetch('/api/auth/profile');
          if (!response.ok) {
            console.log('Initial profile check failed, logging out.');
            await logoutStable();
          } else {
            console.log('Initial profile check successful.');
          }
        } catch (error) {
          console.error('Error during initial auth verification:', error);
          await logoutStable();
        }
      }
    };
    verifyAuth();
  }, [logoutStable]);

  // Proactive token refresh - refresh tokens 5 minutes before they expire
  useEffect(() => {
    if (!token) return;

    const refreshInterval = setInterval(async () => {
      try {
        console.log('Proactively refreshing token...');
        const response = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: {
            'X-CSRF-Token': csrfToken || '',
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const data = await parseJsonResponse(response);
          if (data.csrf_token) {
            setCsrfToken(data.csrf_token);
            console.log('Token refreshed proactively.');
          }
        } else {
          console.warn('Proactive token refresh failed, will retry on next request.');
        }
      } catch (error) {
        console.warn('Proactive token refresh error:', error);
      }
    }, 25 * 60 * 1000); // Refresh every 25 minutes (5 minutes before 30-minute expiry)

    return () => clearInterval(refreshInterval);
  }, [token, csrfToken]);

  const checkTenants = useCallback(async (username: string, password: string): Promise<TenantInfo[]> => {
    try {
      const hostname = window.location.hostname;
      const response = await fetch('/api/auth/check-tenants', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password, hostname }),
      });
      const data = await parseJsonResponse(response);
      setAvailableTenants(data.tenants);
      return data.tenants;
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("Rate limit exceeded") || error.message.includes("429")) {
          throw new Error(`Rate limit exceeded: ${error.message}`);
        }
        throw new Error(error.message || 'Tenant check failed');
      }
      throw new Error('An unknown error occurred during tenant check');
    }
  }, []);

  const login = useCallback(async (username: string, password: string, tenant: string) => {
    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, tenant }),
      });

      const data = await parseJsonResponse(response);

      setUser(data.user);
      setCsrfToken(data.csrf_token);
      setToken('token-in-cookie');

      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/');
    } catch (error) {
      if (error instanceof Error) {
        if (error.message.includes("Rate limit exceeded") || error.message.includes("429")) {
          throw new Error(`Rate limit exceeded: ${error.message}`);
        }
        throw new Error(error.message || 'Login failed');
      }
      throw new Error('An unknown error occurred during login');
    }
  }, [navigate]);

  const logout = logoutStable;

  const register = useCallback(async (username: string, password: string, name: string, tenant: string, secret: string) => {
    try {
      const response = await fetch('/api/auth/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, name, tenant, secret }),
      });

      const data = await parseJsonResponse(response);

      setUser(data.user);
      setCsrfToken(data.csrf_token);
      setToken('token-in-cookie');

      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/');
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Registration failed');
      }
      throw new Error('An unknown error occurred during registration');
    }
  }, [navigate]);

  const addTenant = useCallback(async (username: string, password: string, tenant: string, secret: string) => {
    try {
      const response = await fetch('/api/auth/add-tenant', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password, tenant, secret }),
      });

      const data = await parseJsonResponse(response);

      setUser(data.user);
      setCsrfToken(data.csrf_token);
      setToken('token-in-cookie');

      localStorage.setItem('user', JSON.stringify(data.user));
      navigate('/');
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to add tenant');
      }
      throw new Error('An unknown error occurred while adding tenant');
    }
  }, [navigate]);

  const updateProfile = useCallback(async (name: string) => {
    try {
      const response = await authenticatedFetch('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({ name }),
      });

      await parseJsonResponse(response);

      const updatedUser = { ...user, name } as User;
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to update profile');
      }
      throw new Error('An unknown error occurred while updating profile');
    }
  }, [user]);

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      const response = await authenticatedFetch('/api/auth/profile', {
        method: 'PUT',
        body: JSON.stringify({ current_password: currentPassword, new_password: newPassword }),
      });

      await parseJsonResponse(response);
    } catch (error) {
      if (error instanceof Error) {
        throw new Error(error.message || 'Failed to change password');
      }
      throw new Error('An unknown error occurred while changing password');
    }
  }, []);

  const hydrateFromTrustedLogin = useCallback((payload: TrustedLoginPayload) => {
    setUser(payload.user);
    setCsrfToken(payload.csrfToken);
    setToken('token-in-cookie');
    localStorage.setItem('user', JSON.stringify(payload.user));
  }, []);

  const value = React.useMemo<AuthContextType>(
    () => ({
      user,
      token,
      csrfToken,
      isAuthenticated: !!token,
      availableTenants,
      checkTenants,
      login,
      logout,
      register,
      addTenant,
      updateProfile,
      changePassword,
      setCsrfToken,
      hydrateFromTrustedLogin,
    }),
    [
      user,
      token,
      csrfToken,
      availableTenants,
      checkTenants,
      login,
      logout,
      register,
      addTenant,
      updateProfile,
      changePassword,
      hydrateFromTrustedLogin,
    ]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
